<template>
  <v-card style="overflow: hidden;">
    <v-toolbar flat dense color="secondary" dark>
      <v-toolbar-title class="body-2">{{ title }}</v-toolbar-title>
      <v-spacer />
      <v-btn-toggle
        v-if="dateRangeEditable"
        v-model="selectedDateRange"
        mandatory
        class="date-range-toggle"
      >
        <v-btn
          v-for="(item, idx) in dateRangeShortcuts"
          :key="idx"
          flat
          small
          :value="item.value"
          :title="item.label"
          class="text-capitalize"
        >
          {{ item.shortLabel }}
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>
    <v-layout wrap class="pa-2">
      <v-flex
        v-for="(group, idx) in groups"
        :key="idx"
        v-bind="childFlexProps"
        class="pa-2"
      >
        <div class="group-header">
          <span class="subheading font-weight-medium">{{ group.title }}</span>
          <v-checkbox
            v-if="supportsTripData(group)"
            :input-value="includeTripData[idx]"
            label="Include Trip Data"
            hide-details
            class="mt-0 pt-0"
            @change="val => onTripDataToggle(idx, val)"
          />
        </div>

        <div class="parameter-chips pb-2">
          <v-chip
            v-for="(param, pIdx) in parametersOf(group)"
            :key="pIdx"
            :selected="isSelectedParameter(idx, param)"
            :color="isSelectedParameter(idx, param) ? 'info lighten' : null"
            :dark="isSelectedParameter(idx, param)"
            class="clickable"
            small
            @click="setActiveParameter(idx, param)"
          >
            {{ paramLabel(param) }}
          </v-chip>
        </div>

        <TimeSeriesGraphWidget
          ref="childWidgets"
          :widget-id="`${widgetId}-${idx}`"
          :config="childConfig(idx)"
          :global-date-range="selectedDateRange"
        />
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import Highcharts from 'highcharts'
import TimeSeriesGraphWidget from './TimeSeriesGraphWidget'
import { dateRangeShortcuts } from './dateRange'

export default {
  name: 'MonitoringFeatureGroupTimeSeriesGraphWidget',
  components: {
    TimeSeriesGraphWidget,
  },
  props: {
    widgetId: { type: String, required: true },
    config: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      selectedDateRange: this.config.dateRange || 'pastTwoWeeks',
      includeTripData: [],
      selectedParameter: [],
    }
  },
  computed: {
    title() {
      return this.config.title || 'Monitoring Feature Groups'
    },
    dateRangeEditable() {
      return this.config.dateRangeEditable !== false
    },
    dateRangeShortcuts() {
      return dateRangeShortcuts
    },
    groups() {
      return this.config.groups || []
    },
    columns() {
      return this.config.columns || 1
    },
    childWidth() {
      return Math.floor(12 / this.columns)
    },
    childFlexProps() {
      return {
        xs12: true,
        [`md${this.childWidth}`]: true,
        [`lg${this.childWidth}`]: true,
      }
    },
  },
  created() {
    this.includeTripData = this.groups.map(group => !!group.tripDataEnabled)
    this.selectedParameter = this.groups.map(group => this.resolveDefaultParameter(group))
  },
  methods: {
    parametersOf(group) {
      return group.parameters || []
    },
    paramLabel(param) {
      return typeof param === 'string' ? param : (param.label || param.identifier)
    },
    paramIdentifier(param) {
      return typeof param === 'string' ? param : param.identifier
    },
    timeSeriesOf(group) {
      return group.timeSeries || []
    },
    tripDataOf(group) {
      return group.tripData || []
    },
    resolveDefaultParameter(group) {
      const params = this.parametersOf(group)
      if (!params.length) return null
      const matched = params.find(p => this.paramIdentifier(p) === group.defaultParameter) || params[0]
      return this.paramLabel(matched)
    },
    supportsTripData(group) {
      return this.parametersOf(group).length > 0
    },
    isSelectedParameter(idx, param) {
      return this.selectedParameter[idx] === this.paramLabel(param)
    },
    parameterOf(dataSetIdentifer) {
      const dotIndex = dataSetIdentifer.indexOf('.')
      return dotIndex >= 0 ? dataSetIdentifer.substring(0, dotIndex) : dataSetIdentifer
    },
    labelPartOf(dataSetIdentifer) {
      const dotIndex = dataSetIdentifer.indexOf('.')
      const atIndex = dataSetIdentifer.indexOf('@')
      const start = dotIndex >= 0 ? dotIndex + 1 : 0
      const end = atIndex >= 0 ? atIndex : dataSetIdentifer.length
      return dataSetIdentifer.substring(start, end)
    },
    normalizeLabel(str) {
      return (str || '').replace(/ᵒ/g, '°')
    },
    selectedIdentifier(idx) {
      const group = this.groups[idx] || {}
      const label = this.selectedParameter[idx]
      const param = this.parametersOf(group).find(p => this.paramLabel(p) === label)
      return param ? this.paramIdentifier(param) : label
    },
    filteredSeries(group, label) {
      if (!label) return []
      const normalized = this.normalizeLabel(label)
      return this.timeSeriesOf(group).filter(ts => this.normalizeLabel(this.labelPartOf(ts.dataSetIdentifer)).startsWith(normalized))
    },
    filteredTripData(group, identifier) {
      if (!identifier) return []
      return this.tripDataOf(group).filter(ts => this.parameterOf(ts.dataSetIdentifer) === identifier)
    },
    childConfig(idx) {
      const group = this.groups[idx] || {}
      const label = this.selectedParameter[idx]
      const identifier = this.selectedIdentifier(idx)
      const palette = Highcharts.getOptions().colors
      const series = this.filteredSeries(group, label).map((s, i) => ({
        ...s,
        color: s.color || palette[i % palette.length],
      }))
      const colorByStation = {}
      series.forEach(s => {
        colorByStation[s.locationName] = s.color
      })
      if (this.includeTripData[idx]) {
        const tripSeries = this.filteredTripData(group, identifier).map(s => ({
          ...s,
          type: 'scatter',
          color: colorByStation[s.locationName],
        }))
        series.push(...tripSeries)
      }
      return {
        title: group.title,
        showLegend: group.showLegend !== false,
        supportsRefresh: false,
        dateRangeEditable: false,
        series,
      }
    },
    setActiveParameter(idx, param) {
      this.$set(this.selectedParameter, idx, this.paramLabel(param))
      this.$nextTick(() => {
        const child = this.$refs.childWidgets && this.$refs.childWidgets[idx]
        if (child && child.refresh) {
          child.refresh()
        }
      })
    },
    onTripDataToggle(idx, value) {
      this.$set(this.includeTripData, idx, value)
      this.$nextTick(() => {
        const child = this.$refs.childWidgets && this.$refs.childWidgets[idx]
        if (child && child.refresh) {
          child.refresh()
        }
      })
    },
    async initialize(force = false) {
      const children = this.$refs.childWidgets || []
      await Promise.allSettled(children.map(w => w.initialize(force)))
    },
  },
}
</script>

<style scoped>
.date-range-toggle {
  margin-right: 8px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 4px;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
